import dayjs from 'dayjs';
import { PropTypes } from 'prop-types';
import React, { useCallback, useEffect, useState, useMemo, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { Button } from '@ge/components/button';
import { ConditionalRender } from '@ge/components/conditional-render';
import { DataLoader } from '@ge/components/data-loader';
import { DatePicker } from '@ge/components/datepicker';
import { Icon, Icons } from '@ge/components/icon';
import { Input } from '@ge/components/input';
import { SpinLoader } from '@ge/components/loader';
import { ConfirmationDialog } from '@ge/components/modal';
import { useNotification } from '@ge/components/notification';
import { Select, MenuPlacements } from '@ge/components/select';
import { Severity } from '@ge/components/severity';
import { Textarea } from '@ge/components/textarea/textarea';
import { useEditDamages } from '@ge/feat-inspections/data-hooks/use-damages';
import {
  Capability,
  CasePriority,
  DataLoaderType,
  DateTimeFormats,
  CaseStatus,
  DayJs,
} from '@ge/models/constants';
import { Placements } from '@ge/models/constants';
import { CalmCategoryMapping } from '@ge/models/constants';
import { NotificationType } from '@ge/models/constants';
import { AuthRender } from '@ge/shared/components/auth-render/auth-render';
import {
  DisplayField,
  DisplayLabel,
  FieldGroup,
} from '@ge/shared/components/tasks/task-template-shared';
import { EntityDetailsContext } from '@ge/shared/context/entity-details-context';
import { useActionsMenu } from '@ge/shared/data-hooks';
import useCaseTemplate from '@ge/shared/data-hooks/use-case-template';
import { useUpdateCase, useCloseCase } from '@ge/shared/data-hooks/use-update-case';
import { CaseStatusMenuItems } from '@ge/shared/models/actions-menu-items';
import { getUserRolesFromAccessToken } from '@ge/shared/services/auth'
import { globalColors } from '@ge/tokens';
import { StatusColor } from '@ge/tokens/colors';

import { EntityCollapsiblePanel } from '../entity-details-shared';

const Container = styled.div`
  position: relative;
`;

const FieldWrapper = styled.div`
  width: 17%;
`;

const EditBtnContainer = styled.button`
  position: absolute;
  right: 20px;
  top: 0;
  cursor: pointer;
`;

const DisplayCaseFeild = styled(DisplayField)`
  text-transform: capitalize;
  margin-top: 4px;
  padding-bottom: 13px;
  line-height: 13px;
  font-size: 13px;
  line-height: 18px;
`;

const DisplayCaseTitleValues = styled(DisplayField)`
  margin-top: 7px;
  padding-bottom: 17px;
  font-size: 13px;
  line-height: 18px;
  height: 18px;
  width: 480px;
  a {
    color: inherit;
    text-decoration: underline;
  }
`;

const DisplayCaseDescriptionValues = styled(DisplayField)`
  margin-bottom: 17px;
  font-size: 13px;
  line-height: 18px;
  height: auto;
  //max-height: 72px;
  width: 702.11px;
  overflow: clip;
`;

const EditButton = styled(Icon).attrs((props) => ({
  size: 14,
  color: props.theme.entityDetails.cases.details.iconColor,
  icon: Icons.PENCIL,
}))``;

const descriptionStyles = css`
  color: #f2f2f2;
  font-family: 'Museo Sans';
  font-size: 14px;
  letter-spacing: 0;
  line-height: 18px;
`;

const DescriptionContainer = styled.div`
  padding: 10px 20px 0 20px;
  position: relative;
  overflow: visible;
`;

const DescriptionEditContainer = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.entityDetails.cases.details.rowBorder};
  ${'' /* padding: 10px 20px 0 20px; */}
  position: relative;
  height: auto;
  min-height: 100% !important;
  overflow: visible;
`;

const PaddingElement = styled.div`
  padding-bottom: 26px;
`;

const StyledTitleTextArea = styled(Textarea)`
  ${descriptionStyles};
  box-sizing: border-box;
  height: 28px;
  width: 519px;
  border: 1px solid #2a323b;
  border-radius: 2px;
  background-color: #1c252d;
  box-shadow: inset 0 -1px 2px 0 rgba(0, 0, 0, 0.5);
  resize: none;
  classname: customscroll-track;
  overflow: hidden;
  margin-bottom: 17px;
  ${({ error }) => !!error && `border: 1px solid ${globalColors.red1};`}
`;

const StyledStatusDetailTextArea = styled(Textarea)`
  ${descriptionStyles};
  box-sizing: border-box;
  height: 28px;
  width: 727px;
  border: 1px solid #2a323b;
  border-radius: 2px;
  background-color: #1c252d;
  box-shadow: inset 0 -1px 2px 0 rgba(0, 0, 0, 0.5);
  resize: none;
  classname: customscroll-track;
  overflow: hidden;
  margin-bottom: 17px;
  ${({ error }) => !!error && `border: 1px solid ${globalColors.red1};`}
`;

const StyledTextAreaDescription = styled(Textarea)`
  ${descriptionStyles};
  box-sizing: border-box;
  height: 70px;
  width: 727px;
  border: 1px solid #2a323b;
  border-radius: 2px;
  background-color: #1c252d;
  box-shadow: inset 0 -1px 2px 0 rgba(0, 0, 0, 0.5);
  resize: none;
  overflow: auto;
  margin-bottom: 17px;

  ${({ error }) => !!error && `border: 1px solid ${globalColors.red1};`}
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    height: 37.16px;
    width: 2px;
    border-radius: 2.5px;
    background-color: #445a6c;
  }
  &::-webkit-scrollbar-track {
    height: 68px;
    width: 6px;
    border-radius: 1px;
    background-color: rgba(12, 15, 18, 0.24);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: right;
  margin-top: 10px;
  margin-bottom: 20px;

  button:first-of-type {
    margin-right: 10px;
  }
`;

const StyledInput = styled(Input)`
  border-radius: 3px;
  color: ${globalColors.slate4};
  font-style: italic;
  font-size: 13px;
  width: 120px;
  height: 25px;
`;

const CalendarIconContainer = styled.div`
  position: absolute;
  bottom: 9px;
  right: 4px;
`;

const CalendarIcon = styled(Icon).attrs((props) => ({
  color: props.theme.manage.taskItem.iconColor,
  icon: Icons.CALENDAR,
  size: 10,
}))`
  margin-right: 4px;
`;

const FieldError = styled.div`
  color: ${StatusColor.DANGER};
  margin-top: -10px;
`;

const PrioritySelectLabel = styled.span`
  margin-left: 5px;
  text-transform: capitalize;
`;

const StyledSelect = styled(Select)`
  text-transform: capitalize;
  margin-right: 20px;
  width: 204px;
  height: 25px;
  font-size: 13px;
  line-height: 15px;
  radius: 4px;
  position: relative;
`;

const ErrorMessageContainer = styled.div`
  color: ${({ theme }) => theme.fileUpload.errorTextColor};
`;

const ModalMessage = styled.p`
  margin: 6px 0px;
  font-size: 14px;
  line-height: 16px;
  width: 600px;
`;

const ModalLoader = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 60px;
  width: 600px;
`;

const categoryRank = {
  'Category 0': 0,
  'Category 1': 1,
  'Category 2': 2,
  'Category 3': 3,
  'Category 3a': 3.1,
  'Category 4': 4,
  'Category 5': 5,
};

const normalizeRoleName = (roleName = '') => roleName.split('-').pop()?.trim();

const calmCategoryNames = {
  CAT0: 'Category 0',
  CAT1: 'Category 1',
  CAT2: 'Category 2',
  CAT3: 'Category 3',
  CAT4: 'Category 4',
  CAT5: 'Category 5',
};

export const CaseDescription = ({ caseDetails, isLoading, handleCalmEscalationAndPopUpDamage, similarCalmCaseData }) => {
  const { t } = useTranslation(['entity-details']);
  const { notify } = useNotification();

  const { sfCase, sfPortalUrl } = useContext(EntityDetailsContext);
  const similarCalmCases = similarCalmCaseData?.data?.cases ?? [];
  const similarCaseCreatedDate = similarCalmCases[0]?.createdDate;
  const formattedSimilarCaseCreatedDate = similarCaseCreatedDate
    ? dayjs(similarCaseCreatedDate * 1000).format(DateTimeFormats.SNOOZE_DATE_FORMAT)
    : '';

  const [snoozeDate, setSnoozeDate] = useState();
  const [snoozeViewDate, setSnoozeViewDate] = useState();
  const getAuthorizedMenuItems = useActionsMenu({ menuItems: CaseStatusMenuItems });
  const formattedSnoozeDate = useMemo(
    () =>
      snoozeDate
        ? dayjs(snoozeDate).format(DateTimeFormats.SNOOZE_DATE_FORMAT)
        : caseDetails.snoozeDate
        ? dayjs(caseDetails.snoozeDate).format(DateTimeFormats.SNOOZE_DATE_FORMAT)
        : null,
    [caseDetails.snoozeDate, snoozeDate],
  );

  const getStatusLabel = (status) => {
    const lable = status
      .toLowerCase()
      .split(' ')
      .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
      .join(' ');
    return lable;
  };

  const analysticSource =
    caseDetails.analyticSource !== null && caseDetails.analyticSource === 'CALM'
      ? caseDetails.analyticSource
      : null;

  const [isEditing, setIsEditing] = useState(false);
  const [triggerHeight, setTriggerHeight] = useState(false);
  const { mutate: updateCase, isLoading: isCaseUpdating } = useUpdateCase();
  const { mutateAsync: closeCase, isLoading: isCaseClosing } = useCloseCase();
  const { editDamages, isLoading: isDamageUpdating } = useEditDamages({
    onSuccess: () => {},
    caseId: similarCalmCases[0]?.caseId,
  });
  const isCalmCaseUpdating = isCaseClosing || isDamageUpdating ;
  const [selectedStatus, setCaseStatus] = useState({
    value: caseDetails.status.toUpperCase(),
    label: getStatusLabel(caseDetails.status),
  });
  const [selectedPriority, setCasePriority] = useState({
    value: caseDetails.priority?.toLowerCase(),
    label: (
      <>
        <Severity level={caseDetails.priority?.toLowerCase()} />
        <span className="option-w-icon">{caseDetails.priority?.toLowerCase()}</span>
      </>
    ),
  });

  const [selectedCalmCategory, setCalmCategory] = useState({
    value: caseDetails.escalation,
    label: caseDetails.escalation,
  });
  const [RenderSnoozeDatePicker, setRenderSnoozeDatePicker] = useState(
    caseDetails.status === CaseStatus.SNOOZED ? true : false,
  );

  const [caseTemplateId, setCaseTemplateId] = useState(caseDetails?.caseTemplateId);
  const [userRole, setUserRole] = useState([]);
  const [fetchCaseTemplateData, setfetchCaseTemplateData] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [showCALMCaseAlert, setShowCALMCaseAlert] = useState({
    showAlert: false,
    actionRequired: false,
    message: '',
  });
  const { errors, handleSubmit, register } = useForm({
    mode: 'onChange',
  });

  useEffect(() => {
    const roles = getUserRolesFromAccessToken();
    setUserRole(Array.isArray(roles) ? roles.map(normalizeRoleName).filter(Boolean) : []);
  }, []);

  const isEngineeringAdmin = userRole.includes('Engineering_Admin');

  const prioritySelectOptions = useMemo(
    () =>
      Object.values(CasePriority).map((value) => {
        return {
          value,
          label: (
            <>
              <Severity level={value} />
              <PrioritySelectLabel>{value}</PrioritySelectLabel>
            </>
          ),
        };
      }),
    [],
  );

  const onChange = (date) => {
    setSnoozeDate(date);
  };

  const minDate = useMemo(() => dayjs().add(1, 'day').toDate(), []);

  const statusMenuItemsFormatted = Object.values(getAuthorizedMenuItems([caseDetails])).map(
    (statusMenuItems) => ({
      value: statusMenuItems.title.toUpperCase(),
      label: getStatusLabel(statusMenuItems.title),
    }),
  );

  const handleStatusSelect = (label) => {
    setCaseStatus({ value: label.toUpperCase(), label: label });
    if (label === 'Snooze') {
      setRenderSnoozeDatePicker(true);
    } else {
      setSnoozeDate('');
      setRenderSnoozeDatePicker(false);
    }
  };

  const handlePrioritySelect = (label) => {
    setCasePriority({ value: label.props.children[1].props.children.toLowerCase(), label: label });
  };

  const handleCalmCaseSelect = (label) => {
    const {
      isEscalated: isSimilarCaseEscalated,
      createdBeforeCurrent: currentCaseIsNewerThanSimilar // if similar case is old, then current case is New, vice-versa.
    } = similarCalmCases[0] || {};

    let { damages: currentCaseDamages = [] } = caseDetails || {};
    currentCaseDamages = (currentCaseDamages || []).filter(damage => damage.status === "Open");

    /**
     * If Similar CALM Case exist and is not Escalated.
     *    If Current case is Newer than the similar case, block escalation
     *    else if the current case is older and has open damages, block escalation
     *    Else current case is older and has open NO damages, Force to create damage. (Handled in Click of Save button) 
     */
     if (similarCalmCases.length > 0 && !isSimilarCaseEscalated) {
      if (currentCaseIsNewerThanSimilar) {
        setShowCALMCaseAlert({
          showAlert: true,
          actionRequired: false,
          message: {
            i18key: 'case_panel.similar_calm_case_alert_msg.older_case_exist',
            description: 'Another Older Case exists. Take action on that case before escalating this or close this case.',
            similarCaseId: similarCalmCases[0]?.caseId,
          },
        });
        return false;
      } 
      else if(currentCaseDamages.length > 0){
        setShowCALMCaseAlert({
          showAlert: true,
          actionRequired: false,
          message: {
            i18key: 'case_panel.similar_calm_case_alert_msg.newer_case_exist',
            description: 'Another Newer Case exists. Take action on that or close that case before escalating this case.',
            similarCaseId: similarCalmCases[0]?.caseId,
          },
        });
        return false;
      }
    }


    setCalmCategory({ value: label, label: label });
    if (label === CalmCategoryMapping.MISCLASSIFIED || label === CalmCategoryMapping.SENSORISSUE) {
      setfetchCaseTemplateData({});
      setErrorMessage('');
      setCasePriority({
        value: caseDetails.priority?.toLowerCase(),
        label: (
          <>
            <Severity level={caseDetails.priority?.toLowerCase()} />
            <span className="option-w-icon">{caseDetails.priority?.toLowerCase()}</span>
          </>
        ),
      });
      setCaseTemplateId(caseDetails?.caseTemplateId);
      return false;
    }

    const caseTitle = caseDetails?.caseTemplateId;
    const caseTemplateId = caseTitle?.replace(/CAT\d+/g, label);
    setCaseTemplateId(caseTemplateId);
  };

  const {
    data: caseTemplateData,
    error: isCaseTemplateError,
    isLoading: isCaseTemplateDataLoading,
  } = useCaseTemplate({
    caseTemplateId: caseTemplateId,
    selectedCalmCategory: selectedCalmCategory.value,
  });

  useEffect(() => {
    let errMsg = isCaseTemplateError?.response?.data?.description ?? '';
    if(similarCalmCaseData?.isError) {
      errMsg = similarCalmCaseData?.error?.response?.data?.description ?? 'Something went wrong on fetching similar cases. Please try again later.';
    }
    setErrorMessage(errMsg);
  }, [isCaseTemplateError?.response?.data?.description, similarCalmCaseData?.isError, similarCalmCaseData?.error]);

  const getDate = (inputDate) => {
    if (!inputDate) {
      return null;
    }
    const formatedDate = (inputDate || 0) * 1000;
    const date = dayjs(formatedDate).format(DateTimeFormats.SNOOZE_DATE_FORMAT);
    return date !== DayJs.INVALID_DATE && date;
  };

  useEffect(() => {
    if (caseDetails?.epochSnoozeDate) {
      const epochSnoozeDate = getDate(caseDetails?.epochSnoozeDate);
      setSnoozeViewDate(epochSnoozeDate);
    }
  }, [caseDetails?.epochSnoozeDate]);

  useEffect(() => {
    setCaseStatus({
      value: caseDetails.status.toUpperCase(),
      label: getStatusLabel(caseDetails.status),
    });
    setRenderSnoozeDatePicker(caseDetails.status === CaseStatus.SNOOZED ? true : false);
  }, [caseDetails]);

  useEffect(() => {
    if (caseTemplateData) {
      setfetchCaseTemplateData(caseTemplateData);
      setCasePriority({
        value: caseTemplateData?.priority,
        label: (
          <>
            <Severity level={caseTemplateData?.priority} />
            <span className="option-w-icon">{caseTemplateData?.priority}</span>
          </>
        ),
      });
    }
  }, [caseTemplateData]);

  const toggleIsEditing = useCallback(
    (action) => {
      setfetchCaseTemplateData({});
      setErrorMessage('');
      if(similarCalmCaseData?.isError){
        setErrorMessage(similarCalmCaseData?.error?.response?.data?.description ?? 'Something went wrong on fetching similar cases. Please try again later.');
      }
      setCasePriority({
        value: caseDetails.priority?.toLowerCase(),
        label: (
          <>
            <Severity level={caseDetails.priority?.toLowerCase()} />
            <span className="option-w-icon">{caseDetails.priority?.toLowerCase()}</span>
          </>
        ),
      });
      setCalmCategory({ value: caseDetails.escalation, label: caseDetails.escalation });
      setIsEditing((editing) => !editing);
      setCaseStatus({
        value: caseDetails.status.toUpperCase(),
        label: getStatusLabel(caseDetails.status),
      });
      if (action === 'Cancel') {
        setSnoozeDate('');
      } else {
        setSnoozeDate(caseDetails?.snoozeDate ? new Date(snoozeViewDate) : '');
      }

      setRenderSnoozeDatePicker(caseDetails.status === CaseStatus.SNOOZED ? true : false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      caseDetails.escalation,
      caseDetails.priority,
      caseDetails?.snoozeDate,
      caseDetails.status,
      snoozeViewDate,
    ],
  );

  const calmCaseTypes = useMemo(() => {
    const title = caseDetails?.caseTemplateId || '';

    const hasCAT0 = title?.includes('CAT0');
    const hasAnyCAT345 = ['CAT3', 'CAT4', 'CAT5'].some((cat) => title?.includes(cat));

    let categoriesToInclude = [];

    if (hasCAT0 || hasAnyCAT345) {
      categoriesToInclude = ['CAT3', 'CAT4', 'CAT5'];
      if (hasCAT0) {
        categoriesToInclude.unshift('CAT0');
      }
    }

    return categoriesToInclude.map((key) => ({
      value: CalmCategoryMapping[key],
      label: t('calm_case_category.dynamic.' + key, CalmCategoryMapping[key]),
    }));
  }, [caseDetails.caseTemplateId, t]);


  const handleCALMCaseSave = (calmCaseEsclationPayload) => {
    const saveCalmCaseEscalation = (payload) => {
      updateCase(payload);
      setIsEditing(false);
    };

    const {
      isEscalated: isSimilarCaseEscalated,
      createdBeforeCurrent: currentCaseIsNewerThanSimilar, // if similar case is old, then current case is New, vice-versa.
      damages: similarCaseDamages,
      caseTemplateId: similarCaseTemplateId,
    } = similarCalmCases[0] || {};

    let { damages: currentCaseDamages = [] } = caseDetails || {};
    currentCaseDamages = (currentCaseDamages || []).filter(damage => damage.status === "Open");

    /**
     * If similar CALM cases exist.
     */
    if (similarCalmCases.length > 0) {
      /**
       * If similar case is not escalated and current case created older than similar case.
       *  If no open damages exist in the current case, force to create damage.
       *  else block escalates the current case.
       */


      if (!isSimilarCaseEscalated && !currentCaseIsNewerThanSimilar) {
        if (currentCaseDamages.length === 0) {
          const similarCaseWithSameTemplate = similarCalmCases?.find(calmCase => calmCase.caseTemplateId === caseTemplateId);
          if (similarCaseWithSameTemplate) {
            setShowCALMCaseAlert({
              showAlert: true,
              actionRequired: false,
              message: {
                i18key: 'case_panel.similar_calm_case_alert_msg.similar_case_template',
                description: 'Similar case with the same template exists. Please close either one to proceed ahead with escalation.',
                similarCaseId: similarCaseWithSameTemplate?.caseId,
              },
            });
            return;
          }

          handleCalmEscalationAndPopUpDamage(
            calmCaseEsclationPayload,
            saveCalmCaseEscalation,
          );
        }
        else {
          setShowCALMCaseAlert({
            showAlert: true,
            actionRequired: false,
            message: {
              i18key: 'case_panel.similar_calm_case_alert_msg.newer_case_exist',
              description: 'Another Newer Case exists. Take action on that or close that case before escalating this case.',
              similarCaseId: similarCalmCases[0]?.caseId,
            },
          });
        }
      }

      /**
       * If similar case is escalated and current case created newer than similar case.
       *  If similar case has damages, check for category downgrade.
       *  If category downgrade, show alert and prevent escalation.
       *  Else show alert to confirm closing current case and reflecting escalation category on similar case damages.
       */
      if (isSimilarCaseEscalated && currentCaseIsNewerThanSimilar) {
        if (similarCaseDamages?.[0]) {
          const selectedCategory =
            calmCategoryNames[selectedCalmCategory?.value] || selectedCalmCategory?.value;
          const isCategoryDowngrade =
            categoryRank[selectedCategory] < categoryRank[similarCaseDamages[0].category];

          if (isCategoryDowngrade) {
            setShowCALMCaseAlert({
              showAlert: true,
              actionRequired: false,
              message: {
                i18key: isEngineeringAdmin
                  ? 'case_panel.similar_calm_case_alert_msg.engineering_admin_downgrade'
                  : 'case_panel.similar_calm_case_alert_msg.unauthorized_category_downgrade',
                description: isEngineeringAdmin
                  ? 'To downgrade damage category, please update the damage after closing this new case.'
                  : 'Damage category cannot be downgraded. Please contact an Engineering Admin.',
              },
            });
            return;
          }

          setShowCALMCaseAlert({
            showAlert: true,
            actionRequired: true,
            message: {
              i18key: 'case_panel.similar_calm_case_alert_msg.escalated_case_confirmation_action',
              description: 'Similar Escalated Case exists. Confirm to close this case and reflect escalation on the other old escalated case damage.',
              similarCaseId: similarCalmCases[0]?.caseId,
            },
            calmCaseEsclationPayload,
          });
        }
        else{
          setShowCALMCaseAlert({
            showAlert: true,
            actionRequired: false,
            message: {
              i18key: 'case_panel.similar_calm_case_alert_msg.older_case_exist',
              description: 'Another Older Case exists. Take action on that case before escalating this or close this case.',
              similarCaseId: similarCalmCases[0]?.caseId,
            },
          });
        }
      }
    }
    else { 

      /**
       * No Similar CALM Case Exist
       * If No damage exists force to create damage.
       * Else update the esclation of case 
       */
      if (currentCaseDamages.length === 0) {
        handleCalmEscalationAndPopUpDamage(
          calmCaseEsclationPayload,
          saveCalmCaseEscalation,
        );
      }
      else {
        updateCase(calmCaseEsclationPayload);
        setIsEditing(false);
      }
    }
  }

  const onConfirmCalmCaseAlert = async () => {
    if (showCALMCaseAlert.actionRequired) {
      try {
        const similarCase = similarCalmCases[0];
        const similarCaseDamage = similarCase?.damages?.[0];
        const selectedCategory =
          calmCategoryNames[selectedCalmCategory?.value] || selectedCalmCategory?.value;

        await closeCase({
          id: caseDetails.id,
          asset: caseDetails.asset,
          disposition: 'Others',
          rootCause: 'Similar Case exists. Hence closing this case.'
        });

        if (similarCaseDamage) {
          await editDamages([
            {
              ...similarCaseDamage,
              category: selectedCategory,
            },
          ]);

          const updatePayload = {
            ...showCALMCaseAlert?.calmCaseEsclationPayload,
            id: similarCase?.caseId,
          };
          delete updatePayload.snoozeDate;
          updateCase(updatePayload);
        }

        setShowCALMCaseAlert({ showAlert: false, actionRequired: false, message: '' });
        setIsEditing(false);
      } catch (error) {
        console.error('Failed to confirm CALM case alert:', error);
        const message =
          error?.response?.data?.description ||
          'Something went wrong. Please try again later';
        notify({ message, type: NotificationType.FAILURE });
      }
    }
    else{
      setShowCALMCaseAlert({ showAlert: false, actionRequired: false, message: '' });
    }
  };

  const onSubmit = useCallback(
    (values) => {
      const calmCaseEsclationPayload = {
        id: caseDetails.id,
        asset: {
          id: caseDetails.asset.id,
        },
        body: values.caseDescription?.trim(),
        description: values.caseTitle,
        snoozeDate: snoozeDate ? snoozeDate.getTime() / 1000 : null,
        status: selectedStatus.label.toUpperCase(),
        priority: selectedPriority.value.toLowerCase(),
        escalation: selectedCalmCategory?.value ? selectedCalmCategory?.value : null,
        analyticSource: analysticSource,
        caseTemplateId: caseTemplateId,
        statusDetails: {
          statusDetail: values.statusDetail,
        },
      }

      const { analyticSource, escalation } = caseDetails || {};
      if (analyticSource === 'CALM' && escalation !== selectedCalmCategory.label) {
        handleCALMCaseSave(calmCaseEsclationPayload);
      }
      else {
        updateCase(calmCaseEsclationPayload);
        setIsEditing(false);
      }
    },
    [
      analysticSource,
      caseDetails.asset.id,
      caseDetails.id,
      caseTemplateId,
      selectedCalmCategory?.value,
      selectedPriority.value,
      selectedStatus.label,
      snoozeDate,
      isEngineeringAdmin,
      similarCalmCaseData,
      editDamages,
      updateCase,      
    ],
  );

  useEffect(() => {
    setTriggerHeight((t) => !t);
  }, [isCaseUpdating, isEditing, isLoading]);

  const formateCaseDescription = (text) => {
    return text?.split('\n').map((line, index) => {
      const isUrl = /^https?:\/\/[^\s]+$/.test(line);
      if (isUrl) {
        return (
          <span key={index}>
            <a href={line} target="_blank" rel="noopener noreferrer">
              {line}
            </a>
            <br />
          </span>
        );
      } else {
        return (
          <span key={index}>
            {line}
            <br />
          </span>
        );
      }
    });
  };

  return (
    <Container>
      <AuthRender
        capability={Capability.CASES_RECOMMENDATIONS}
        edit
        description="Edit case description button"
        siteLevel={false}
      >
        {!isEditing && !isCaseUpdating && (
          <EditBtnContainer onClick={() => toggleIsEditing('Edit')}>
            <EditButton />
          </EditBtnContainer>
        )}
      </AuthRender>
      <EntityCollapsiblePanel
        headerContent={<h2>{t('case_panel.case_details', 'Case Detail')}</h2>}
        expanded={true}
        triggerHeight={triggerHeight}
      >
        <DataLoader type={DataLoaderType.TABLE} isLoading={isCaseUpdating}>
          <DescriptionContainer>
            {isEditing ? (
              <DescriptionEditContainer>
                <DisplayLabel>{t('Title*')}</DisplayLabel>
                {caseDetails?.analyticSource === 'CALM' ? (
                  <StyledTitleTextArea
                    id="case-title-textarea"
                    placeholder={t('case_panel.description_placeholder', 'Add a  description')}
                    name="caseTitle"
                    value={
                      fetchCaseTemplateData.title
                        ? fetchCaseTemplateData.title
                        : caseDetails.description
                    }
                    ref={register({ maxLength: 1000 })}
                    error={errors.caseTitle}
                  />
                ) : (
                  <StyledTitleTextArea
                    id="case-title-textarea"
                    placeholder={t('case_panel.description_placeholder', 'Add a  description')}
                    name="caseTitle"
                    defaultValue={caseDetails.description}
                    ref={register({ maxLength: 1000 })}
                    error={errors.caseTitle}
                  />
                )}

                <DisplayLabel>{t('Description')}</DisplayLabel>
                {caseDetails?.analyticSource === 'CALM' ? (
                  <StyledTextAreaDescription
                    id="case-description-textarea"
                    placeholder={t('case_panel.description_placeholder', 'Add a description')}
                    name="caseDescription"
                    value={
                      fetchCaseTemplateData.description
                        ? fetchCaseTemplateData.description
                        : caseDetails.body
                    }
                    ref={register()}
                  />
                ) : (
                  <StyledTextAreaDescription
                    id="case-description-textarea"
                    placeholder={t('case_panel.description_placeholder', 'Add a description')}
                    name="caseDescription"
                    defaultValue={caseDetails.body}
                    ref={register()}
                  />
                )}
                <FieldGroup>
                  <div className="field-wrapper">
                    <DisplayLabel>{t('Priority')}</DisplayLabel>
                    <StyledSelect
                      name="casepriority"
                      onChange={(option) => handlePrioritySelect(option.label)}
                      options={prioritySelectOptions}
                      value={selectedPriority}
                      minWidth={150}
                      marginRight={20}
                      menuPlacement={MenuPlacements.TOP}
                      maxMenuHeight={200}
                    />
                  </div>
                  <div className="field-wrapper">
                    <DisplayLabel>{t('Status')}</DisplayLabel>
                    <StyledSelect
                      name="casestatus"
                      onChange={(option) => handleStatusSelect(option.label)}
                      options={statusMenuItemsFormatted}
                      value={selectedStatus}
                      minWidth={150}
                      marginRight={20}
                      menuPlacement={MenuPlacements.TOP}
                    />
                  </div>
                  <ConditionalRender shouldRender={RenderSnoozeDatePicker}>
                    <div className="field-wrapper">
                      <DatePicker
                        name="snoozedate"
                        selected={snoozeDate}
                        startDate={snoozeDate}
                        minDate={minDate}
                        onChange={onChange}
                        popperPlacement={Placements.LEFT}
                        customInput={
                          <div>
                            <StyledInput
                              autoComplete="off"
                              label={t('case_panel.snooze_until', 'Snooze until')}
                              onChange={() => null}
                              required
                              value={snoozeDate ? formattedSnoozeDate : ''}
                            />
                            <CalendarIconContainer>
                              <CalendarIcon />
                            </CalendarIconContainer>
                          </div>
                        }
                      />
                      {/* <PaddingElementforEditScreen /> */}
                    </div>
                  </ConditionalRender>
                  {caseDetails?.analyticSource !== null &&
                    caseDetails?.analyticSource === 'CALM' && (
                      <div
                        className={
                          RenderSnoozeDatePicker
                            ? 'field-wrapper conditional-render'
                            : 'field-wrapper'
                        }
                      >
                        <DisplayLabel>{t('Escalation*')}</DisplayLabel>
                        <StyledSelect
                          maxWidth={110}
                          minWidth={110}
                          name="calmcasecategory"
                          options={calmCaseTypes}
                          onChange={(option) => handleCalmCaseSelect(option.label)}
                          value={selectedCalmCategory}
                          menuPlacement={MenuPlacements.TOP}
                          disabled={similarCalmCaseData?.isError}
                        />
                      </div>
                    )}
                </FieldGroup>
                {caseDetails?.analyticSource !== null &&
                  caseDetails?.analyticSource === 'CALM' && (
                    <ConfirmationDialog
                      confirmLabel={t('general:ok', 'OK')}
                      cancelLabel={
                        !isCalmCaseUpdating && showCALMCaseAlert.actionRequired
                          ? t('general:cancel', 'Cancel')
                          : ''
                      }
                      isOpen={showCALMCaseAlert.showAlert}
                      header={t('case_panel.calm_case.alert_header_text', 'Alert')}
                      onCancel={
                        isCalmCaseUpdating
                          ? undefined
                          : () => setShowCALMCaseAlert({ showAlert: false, actionRequired: false, message: '' })
                      }
                      onConfirm={isCalmCaseUpdating ? undefined : onConfirmCalmCaseAlert}
                    >
                      {isCalmCaseUpdating ? (
                        <ModalLoader>
                          <SpinLoader showLoader />
                        </ModalLoader>
                      ) : (
                        <ModalMessage>
                          {t(showCALMCaseAlert.message.i18key, {
                            caseId: showCALMCaseAlert.message?.similarCaseId,
                            createdDate: formattedSimilarCaseCreatedDate,
                            defaultValue: showCALMCaseAlert.message.description,
                          })}
                        </ModalMessage>
                      )}
                    </ConfirmationDialog>
                  )}
                <DisplayLabel>{t('case_panel.status_detail', 'Status Detail')}</DisplayLabel>
                <StyledStatusDetailTextArea
                  id="case-statusDetail-textarea"
                  placeholder={t(
                    'case_panel.statusDetail_placeholder',
                    'Type status detail here...',
                  )}
                  name="statusDetail"
                  defaultValue={caseDetails?.statusDetails?.statusDetail}
                  ref={register({
                    maxLength: {
                      value: 200,
                      message: 'Maximum 200 characters are allowed.',
                    },
                  })}
                  error={errors.statusDetail}
                />
                {errors?.statusDetail?.message && (
                  <FieldError>{errors.statusDetail.message}</FieldError>
                )}
                {errorMessage &&
                selectedCalmCategory.value !== CalmCategoryMapping.SENSORISSUE &&
                selectedCalmCategory.value !== CalmCategoryMapping.MISCLASSIFIED ? (
                  <ErrorMessageContainer>{errorMessage}</ErrorMessageContainer>
                ) : null}
                <DisplayLabel>{t('Salesforce Case Id')}</DisplayLabel>
                <DisplayCaseTitleValues>
                  <a
                    href={`${sfPortalUrl}/${caseDetails.sfCaseId || sfCase}/view`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {caseDetails.sfCaseId || sfCase}
                  </a>
                </DisplayCaseTitleValues>
                <ActionButtons>
                  <Button onClick={() => toggleIsEditing('Cancel')}>
                    {t('general:cancel', 'Cancel')}
                  </Button>
                  <Button
                    disabled={
                      (caseDetails.analyticSource === 'CALM' &&
                        selectedCalmCategory.value === null) ||
                      (caseDetails.analyticSource === 'CALM' && errorMessage !== '') ||
                      (selectedStatus.value === CaseStatus.SNOOZED &&
                        (snoozeDate === undefined || !snoozeDate)) ||
                      isCaseTemplateDataLoading ||
                      isCaseTemplateError
                    }
                    primary
                    onClick={handleSubmit(onSubmit)}
                  >
                    {t('general:save', 'Save')}
                  </Button>
                </ActionButtons>
              </DescriptionEditContainer>
            ) : (
              <>
                <DescriptionEditContainer>
                  <DisplayLabel>{t('Title')}</DisplayLabel>
                  <DisplayCaseTitleValues>{caseDetails.description}</DisplayCaseTitleValues>
                  <DisplayLabel>{t('Description')}</DisplayLabel>
                  <DisplayCaseDescriptionValues>
                    <span style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                      {formateCaseDescription(caseDetails.body)}
                    </span>
                  </DisplayCaseDescriptionValues>
                  <FieldGroup>
                    <FieldWrapper className="field-wrapper">
                      <DisplayLabel>{t('Priority')}</DisplayLabel>
                      <DisplayCaseFeild>{caseDetails.priority?.toLowerCase()}</DisplayCaseFeild>
                    </FieldWrapper>
                    <FieldWrapper className="field-wrapper">
                      <DisplayLabel>{t('Status')}</DisplayLabel>
                      <DisplayCaseFeild>{caseDetails.status?.toLowerCase()}</DisplayCaseFeild>
                    </FieldWrapper>
                    {caseDetails?.analyticSource !== null &&
                      caseDetails?.analyticSource === 'CALM' && (
                        <FieldWrapper className="field-wrapper">
                          <DisplayLabel>{t('Escalation')}</DisplayLabel>
                          <DisplayCaseFeild>{caseDetails.escalation}</DisplayCaseFeild>
                        </FieldWrapper>
                      )}
                    <ConditionalRender shouldRender={caseDetails.snoozeDate}>
                      <FieldWrapper className="field-wrapper">
                        {selectedStatus?.value !== CaseStatus.SNOOZED ? (
                          ''
                        ) : (
                          <>
                            <DisplayLabel>
                              {t('case_panel.snooze_until', 'Snooze Until')}
                            </DisplayLabel>
                            <DisplayCaseFeild>{snoozeViewDate}</DisplayCaseFeild>
                          </>
                        )}
                      </FieldWrapper>
                    </ConditionalRender>
                  </FieldGroup>
                  <DisplayLabel>
                    {t('case_panel.status_detail', 'Status Detail')}{' '}
                    {caseDetails?.statusDetails?.lastUpdatedDate &&
                      `(Last Updated On: ${dayjs(
                        caseDetails?.statusDetails?.lastUpdatedDate * 1000,
                      ).format(DateTimeFormats.ISSUE_DETAIL_DATE_TIME)})`}
                  </DisplayLabel>
                  <DisplayCaseTitleValues>
                    {caseDetails?.statusDetails?.statusDetail}
                  </DisplayCaseTitleValues>
                  <DisplayLabel>{t('Salesforce Case Id')}</DisplayLabel>
                  <DisplayCaseTitleValues>
                    <a
                      href={`${sfPortalUrl}/${caseDetails.sfCaseId || sfCase}/view`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {caseDetails.sfCaseId || sfCase}
                    </a>
                  </DisplayCaseTitleValues>
                  <PaddingElement />
                </DescriptionEditContainer>
              </>
            )}
          </DescriptionContainer>
        </DataLoader>
      </EntityCollapsiblePanel>
    </Container>
  );
};

CaseDescription.propTypes = {
  caseDetails: PropTypes.instanceOf(Object).isRequired,
  isLoading: PropTypes.bool.isRequired,
};
